import { Button, InfoNote, Modal } from '@ghp/ui'
import { Camera, FolderOpen, Info } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import s from './CameraModal.module.css'

/**
 * Le viseur de l'app, pour les navigateurs qui ignorent `capture` : Chrome et Edge sous
 * Windows ouvraient l'explorateur de fichiers au lieu de l'appareil (GHP, 30/10). Android
 * garde l'appareil du système, qui y fonctionne.
 *
 * La photo sort en JPEG, comme celle d'un appareil : elle suit ensuite le même chemin.
 */
export interface CameraModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onShot: (file: File) => void
  /** L'appareil ne s'ouvre pas : l'agent choisit la photo dans les fichiers de la tablette. */
  onFallback: () => void
}

/** Le navigateur ouvre-t-il l'appareil du système sur `capture` ? Seuls les mobiles le font. */
export function needsInAppCamera(): boolean {
  return (
    !/Android|iPhone|iPad/i.test(navigator.userAgent) &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  )
}

export function CameraModal({ open, onOpenChange, onShot, onFallback }: CameraModalProps) {
  const video = useRef<HTMLVideoElement>(null)
  const [ready, setReady] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setReady(false)
    setFailure(null)
    let stream: MediaStream | null = null
    let closed = false
    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      })
      .then((opened) => {
        stream = opened
        if (closed) return opened.getTracks().forEach((track) => track.stop())
        if (video.current !== null) video.current.srcObject = opened
        setReady(true)
      })
      .catch((error: unknown) => {
        if (!closed) setFailure(failureOf(error))
      })
    return () => {
      closed = true
      stream?.getTracks().forEach((track) => track.stop())
    }
  }, [open])

  function shoot() {
    const frame = video.current
    if (frame === null || frame.videoWidth === 0) return
    const canvas = document.createElement('canvas')
    canvas.width = frame.videoWidth
    canvas.height = frame.videoHeight
    canvas.getContext('2d')?.drawImage(frame, 0, 0)
    canvas.toBlob(
      (blob) => {
        if (blob === null) return setFailure('La photo n’a pas pu être enregistrée. Reprenez-la.')
        onShot(new File([blob], `photo-${String(Date.now())}.jpg`, { type: 'image/jpeg' }))
      },
      'image/jpeg',
      0.9,
    )
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      width="full"
      icon={<Camera size={18} />}
      title="Prendre une photo"
      footer={
        <>
          {failure === null ? (
            <Button tone="brand" disabled={!ready} onClick={shoot}>
              <Camera size={18} aria-hidden />
              Prendre la photo
            </Button>
          ) : (
            <Button tone="brand" onClick={onFallback}>
              <FolderOpen size={18} aria-hidden />
              Choisir dans les fichiers
            </Button>
          )}
          <Button
            tone="ghost"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Annuler
          </Button>
        </>
      }
    >
      {failure === null ? (
        <video ref={video} className={s.viewfinder} autoPlay playsInline muted />
      ) : (
        <InfoNote tone="warn" icon={<Info size={15} />} role="alert">
          {failure}
        </InfoNote>
      )}
    </Modal>
  )
}

function failureOf(error: unknown): string {
  const name = error instanceof DOMException ? error.name : ''
  if (name === 'NotAllowedError')
    return 'L’accès à l’appareil photo est refusé. Autorisez-le dans le navigateur (cadenas à gauche de l’adresse), ou choisissez la photo dans les fichiers de la tablette.'
  if (name === 'NotFoundError')
    return 'Aucun appareil photo trouvé sur cette tablette. Choisissez la photo dans ses fichiers.'
  return 'L’appareil photo ne s’ouvre pas — une autre application l’utilise peut-être. Fermez-la, ou choisissez la photo dans les fichiers de la tablette.'
}
